import type { UseMutationResult } from "@tanstack/react-query";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";

interface Props<T extends FieldValues> {
  methods: UseFormReturn<T, any>;
  mutation: UseMutationResult<void, Error, T, unknown>;
  btnText: string;
  children: ReactNode;
  cancelPath?: string;
}

function parseError(error: Error | null): string[] {
  if (!error) return [];

  const errorMessage = error.message || error.toString();

  try {
    const jsonMatch = errorMessage.match(/: ({.+})$/);
    if (jsonMatch) {
      const errorData = JSON.parse(jsonMatch[1]);

      if (errorData.errors) {
        const errorMessages: string[] = [];
        Object.keys(errorData.errors).forEach((field) => {
          const fieldError = errorData.errors[field];
          if (fieldError && fieldError.msg) {
            errorMessages.push(`${field}: ${fieldError.msg}`);
          } else if (fieldError && typeof fieldError === "string") {
            errorMessages.push(`${field}: ${fieldError}`);
          }
        });
        if (errorMessages.length > 0) {
          return errorMessages;
        }
      }

      if (errorData.message) {
        return [errorData.message];
      }
    }
  } catch {
    // Fall through to default handling
  }

  return [errorMessage];
}

function SubmitPanel({ children }: { children: ReactNode }) {
  return (
    <div
      id="submit-panel"
      className="fixed bottom-0 left-0 right-0 bg-white dark:bg-her-darkblue2 flex items-center justify-end gap-8 px-2 md:px-10 py-3 shadow-[10px_4px_10px_0px_#0000001A] border-t border-gray-200 dark:border-gray-600"
    >
      {children}
    </div>
  );
}

function LoadingOvalIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" className={`stroke-slate-400 dark:stroke-white ${className}`}>
      <g fill="none" fillRule="evenodd">
        <g transform="translate(1 1)" strokeWidth="2">
          <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
          <path d="M36 18c0-9.94-8.06-18-18-18">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      </g>
    </svg>
  );
}

function ExclamationTriangleIcon({ className = "w-6 h-6" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
      />
    </svg>
  );
}

export default function GenericFormEditor<T extends FieldValues>({
  methods,
  mutation,
  btnText,
  children,
  cancelPath,
}: Props<T>) {
  const { error } = mutation;
  const errorMessages = parseError(error);
  const { push, back } = useRouter();
  const { t, msg } = useTranslation();

  async function onSubmit(data: T) {
    mutation.mutate(data);
  }

  function handleCancel() {
    if (cancelPath) {
      void push(cancelPath);
    } else {
      back();
    }
  }

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
      {errorMessages.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <p className="text-red-600 dark:text-red-400 font-semibold mb-2">Error:</p>
          <ul className="list-disc list-inside space-y-1">
            {errorMessages.map((line, idx) => (
              <li key={idx} className="text-red-600 dark:text-red-400 text-sm">
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
      {children}

      <SubmitPanel>
        <button data-action="cancel" type="button" onClick={handleCancel}>
          {t(msg.form.cancel)}
        </button>
        {errorMessages.length > 0 && (
          <div className="text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
          </div>
        )}
        <input type="submit" value={btnText} disabled={mutation.isPending} />
        {mutation.isPending && <LoadingOvalIcon />}
      </SubmitPanel>
    </form>
  );
}
